import { EventEmitter } from '@angular/core';
import { L10nTranslationService } from 'angular-l10n';
import { BehaviorSubject, debounceTime, Subscription } from 'rxjs';
import { SegmentedMessage } from 'sms-segments-calculator';
import {
  AlertDefaults,
  AlertsApi,
  PreviewTextMessageRequest,
  PreviewTextMessageResponse,
  TextMessageChannel,
} from 'src/app/hickory-api';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { AccessControl } from 'src/app/shared/authentication/access-control';
import { PlaceholderUtilities } from 'src/app/shared/templated-input/placeholder-utilities';
import { AlertChannelsHelper, AlertMessageHelper, AlertFields, AlertModelService, AlertSenderHelper } from '..';
import { AvailableTemplatesService } from 'src/app/shared/templated-input/available-templates.service';

const TAG = 'AlertTextMessageHelper';

export class TextMessagePreviewData {
  private alert: AlertDefaults;
  private preview?: PreviewTextMessageResponse;

  messageLength: number = 0;
  segmentCount: number = 1;
  truncatedMessage?: string;

  get channel(): TextMessageChannel {
    return this.alert?.channels?.textMessage;
  }
  get channelMessage(): string {
    return this.channel?.message ?? this.alert?.message?.basic?.message ?? '';
  }
  get previewMessage(): string {
    return this.preview?.message;
  }
  get characterLimit(): number {
    return this.preview?.maxMessageLength;
  }

  get characterLimitExceeded(): boolean {
    return !!this.characterLimit && this.messageLength > this.characterLimit;
  }
  get characterRecommendationExceeded(): boolean {
    return this.messageLength > AlertTextMessageHelper.SMS_CHAR_RECOMMENDATION;
  }
  get segmentThresholdExceeded(): boolean {
    return (
      this.segmentCount > AlertTextMessageHelper.SMS_MAX_SEGMENTS &&
      this.channel?.deliveryMethod !== TextMessageChannel.DeliveryMethodEnum.Smtp
    );
  }

  updateStats(alert: AlertDefaults, preview: PreviewTextMessageResponse) {
    this.alert = alert;
    if (preview) {
      this.preview = preview;
      this.messageLength = preview.fullMessageLength;
      this.truncatedMessage = preview.bodyText;
      const segmented = new SegmentedMessage(preview.message ?? '');
      this.segmentCount = segmented.segmentsCount;
    }
  }
}

export class AlertTextMessageHelper {
  static readonly SMS_MAX_SEGMENTS = 2;
  static readonly SMS_CHAR_RECOMMENDATION = 320;

  previewSubject = new BehaviorSubject<TextMessagePreviewData>(null);
  responseHasError: boolean = false;
  get channel(): TextMessageChannel {
    return this.alertModel.alertChannels?.textMessage;
  }

  get alertMessage(): string {
    return this.alertModel.basicMessage?.message || '';
  }
  // ROAD-6773: We need to use the '??' operator here to check for null or
  // undefined rather than the '||' operator, which also checks for non-empty
  // length so that if the user back-spaces over the message to clear it, it
  // won't immediately revert to the alert message.
  get channelMessage(): string {
    return this.channel?.message ?? this.alertMessage;
  }
  get loadingPreview(): boolean {
    return !!this.previewRequest$;
  }
  get latestData(): TextMessagePreviewData {
    return this.previewSubject.value;
  }
  get isLimitExceeded(): boolean {
    return !!this.latestData?.characterLimitExceeded;
  }

  get isUsingPersonalization(): boolean {
    const personalizationEnabled = this.alertModel?.alertMessage?.customMessageVariables?.useMessageVariables ?? false;
    const tenantHasPersonalization =
      this.authService?.hasPermission(AccessControl.FeatureMessagePersonalization) ?? false;
    return personalizationEnabled && tenantHasPersonalization;
  }

  private readonly changes: EventEmitter<string> = new EventEmitter();

  private lastRequest: PreviewTextMessageRequest;
  private previewRequest$: Subscription;
  private readonly subscription = new Subscription();

  constructor(
    private readonly alertModel: AlertModelService,
    private readonly placeholderService: AvailableTemplatesService,
    private readonly authService: AuthService = alertModel.authService,
    private readonly alertsApi: AlertsApi = alertModel.alertsApi,
  ) {
    // add our subscription to the alert model service's so that we get cleaned up when the service is reset or disposed.
    this.alertModel.subscription.add(this.subscription);

    // subscribe to all the events that can affect the text message
    this.subscription.add(
      this.alertModel.modelEvents.subscribe((event) => {
        switch (event) {
          case AlertFields.ALL_FIELDS:
          case AlertFields.CHANNELS:
          case AlertFields.MESSAGE:
          case AlertFields.SUBJECT:
          case AlertFields.SENDER:
            this.changes.emit();
            break;
        }
      }),
    );

    // subscribe twice to the changes emitter:
    //  1) "immediate" without a debounce so that our character counter updates as the user types
    //  2) "debounced" with a debouncer so that we don't spam our API
    const debounceInterval = 500; // 500ms
    this.subscription.add(this.changes.subscribe((_) => this.onMessageUpdatedImmediate()));
    this.subscription.add(
      this.changes.pipe(debounceTime(debounceInterval)).subscribe((_) => this.onMessageUpdateDebounced()),
    );
  }

  onMessageUpdatedImmediate() {
    const message = this.channelMessage || this.alertMessage;
    // short circuit if we don't have a message or if we're not configured to send it
    if (!message?.length || !this.channel?.send) {
      this.lastRequest = null;
      this.previewSubject.next(null);
      return;
    }

    // send a preliminary update based on crude calculations so can update the counter while we wait for the API response
    const alert = this.alertModel.alertSettings;
    const data = this.latestData ?? new TextMessagePreviewData();
    data.updateStats(alert, null);
    this.previewSubject.next(data);
  }

  onMessageUpdateDebounced() {
    const message = this.channelMessage || this.alertMessage;
    // short circuit if we don't have a message or if we're not configured to send it
    if (!message?.length || !this.channel?.send) {
      this.lastRequest = null;
      this.previewSubject.next(null);
      return;
    }

    const alert = this.alertModel.alertSettings;
    const data = this.latestData ?? new TextMessagePreviewData();
    const baseReq: PreviewTextMessageRequest = {
      subject: this.alertModel.basicMessage?.subject,
      message: message,
      externalLink: this.alertModel.basicMessage?.externalLink,
      agencyName: this.authService.user?.snapshot?.agency,
      senderName: AlertSenderHelper.ensureDisplayName(this.alertModel),
    };

    if (this.isUsingPersonalization) {
      const placeholders = PlaceholderUtilities.extractPlaceholders(message);

      this.placeholderService.getAllAvailableTemplates(this.alertModel?.alertTemplate?.id).subscribe((result) => {
        const requestVariables = placeholders.reduce(
          (acc, placeholder) => {
            acc[placeholder] = AlertMessageHelper.computePlaceholderValue(placeholder, this.alertModel, result.items);
            return acc;
          },
          {} as Record<string, string>,
        );

        const req: PreviewTextMessageRequest = {
          ...baseReq,
          variables: requestVariables,
        };

        // Move the duplicate check inside the subscription where we have the complete request
        if (JSON.stringify(this.lastRequest) === JSON.stringify(req)) return;
        this.lastRequest = req;

        this.executePreviewRequest(req, alert, data);
      });
    } else {
      // For non-personalized messages, check for duplicates immediately
      if (JSON.stringify(this.lastRequest) === JSON.stringify(baseReq)) return;
      this.lastRequest = baseReq;

      this.executePreviewRequest(baseReq, alert, data);
    }
  }

  private executePreviewRequest(req: PreviewTextMessageRequest, alert: AlertDefaults, data: TextMessagePreviewData) {
    this.previewRequest$?.unsubscribe(); // cancel inflight request, if any

    LogHelper.debug(TAG, 'updating stats...');
    const wasLimitExceeded = data.characterLimitExceeded;
    const rx$ = this.alertsApi.getAlertsTextMessagePreview(req).subscribe({
      next: (res) => {
        data.updateStats(alert, res?.item);
        LogHelper.debug(
          TAG,
          'updated stats',
          `exceeded=${data.characterLimitExceeded}`,
          `messageLength=${data.messageLength}`,
        );
        this.previewSubject.next(data);
        // if the validation status changed, notify the service so that it can update the validation errors on the preview
        // component
        if (data.characterLimitExceeded !== wasLimitExceeded) {
          LogHelper.debug(
            TAG,
            `notifying on validation status change: status=${data.characterLimitExceeded ? 'invalid' : 'OK'}`,
          );
          this.alertModel.notifyFieldUpdated(AlertFields.MESSAGE);
        }
      },
      error: (_err) => {
        this.lastRequest = null;
        this.previewRequest$ = undefined;
        this.responseHasError = true;
      },
      complete: () => {
        this.previewRequest$ = undefined;
        this.responseHasError = false;
      },
    });

    this.previewRequest$ = rx$;
    this.subscription.add(rx$);
  }

  buildCharacterCountText(translation: L10nTranslationService): string {
    const data = this.latestData;
    return AlertChannelsHelper.buildCharacterCountText(translation, data?.messageLength, data?.characterLimit);
  }

  buildCharacterCountTip(translation: L10nTranslationService): string {
    const params = {
      characters: this.latestData?.messageLength ?? 0,
      segments: this.latestData?.segmentCount ?? 1,
    };

    return translation.translate('Alert.Channels.CharacterSmsRecommendationTip', params);
  }

  buildValidationError(translation: L10nTranslationService): string {
    const data = this.latestData;
    return AlertChannelsHelper.buildCharacterLimitValidationError(
      translation,
      AlertChannelsHelper.KEY_TEXT_MESSAGE,
      data?.messageLength,
      data?.characterLimit,
    );
  }
}
